/*eslint-disable @typescript-eslint/member-ordering */
import { LookupItem, MarketItem, StationItem, ChannelItem, SegmentItem } from '../lookup';
import { FilterTemplate, SelectFilter, StringFilter, TimeFilter } from '@models/filter-types';
import { UiConfig } from '@models/config';
import { cascadeToFilter } from '@shared/helpers/functions/filter-helpers';
import { UrlStore } from '@shared/helpers/constants/url-store';
import { LookupV2Service } from '@services/lookup-v2/lookup-v2.service';
import { marketsFilter, stationsFilter, channelsFilter } from '@models/base_filters';
import { FilterSettings } from '@models/filter';
import { EditableSegment } from '@models/rate-card/rate-card';
import { cloneDeep } from 'lodash';

export class CommonSegmentFilters implements FilterTemplate {
    public marketItems: MarketItem[] = [];
    private stationItems: StationItem[] = [];
    private channelItems: ChannelItem[] = [];
    public loading!: boolean;

    public dynamicFilters: { [segmentType: string]: SelectFilter<SegmentItem> | StringFilter | TimeFilter } = {};
    constructor(
        private lookupSvc: LookupV2Service,
        public config: UiConfig,
        private tabNumber: number,
        private channelGroupId: number,
        private marketId: number,
        private stationId: number,
        private channelId: number,
        private segmentData?: EditableSegment,
    ) {}

    settings: FilterSettings = {
        config: this.config,
        lookupSvc: this.lookupSvc,
        hasDynamicRates: true,
        tabNumber: this.tabNumber,
        screenName: UrlStore.screenNames.rateCard,
    };
    isSegmentData = this.segmentData ? true : false;
    isMarketsToggle = this.isSegmentData || !this.settings.config?.roleCapability?.rate_optics.general.markets;
    isStationsToggle = this.isSegmentData || !this.settings.config.roleCapability.rate_optics.general.station;
    isChannelsToggle = this.isSegmentData || !this.settings.config?.roleCapability?.rate_optics?.general?.channel;

    markets = marketsFilter(this.settings)
        .OnLoad((items: MarketItem[]) => {
            if (this.marketItems.length === 0) {
                this.marketItems = cloneDeep(items);
            }
            if (this.channelGroupId !== null) {
                const newOptions = this.marketItems.filter((i) => i.channelGroups.includes(this.channelGroupId));
                this.markets.Options(newOptions);
                if (this.markets.Value.length === 0 && newOptions.length === 1) {
                    this.markets.Default(items);
                }
            }
            if (this.marketId) {
                const selectd = items.filter((item) => item.id === this.marketId);
                this.markets.Default(selectd);
                this.markets.disabledOptions = selectd;
            }
        })

        .OnChange(() => {
            if (!this.markets.isOpened) {
                // when the market changes we filter the list of stations to match
                if (this.stations) {
                    this.updateStations();
                }
            }
        })
        .CompareFn((lhs: MarketItem, rhs: MarketItem) => lhs.id === rhs.id)
        .Required(false)
        .Toggle(this.isMarketsToggle);

    stations = stationsFilter(this.settings)
        // load the list of stations and filter by selected markets
        .OnLoad((items: StationItem[]) => {
            // store the full list of stations as the base for filters
            if (this.stationItems.length === 0) {
                this.stationItems = cloneDeep(items);
            }
            this.updateStations();
            if (this.stationId) {
                const selectd = items.filter((item) => item.id === this.stationId);
                this.stations.Default(selectd);
                this.stations.disabledOptions = selectd;
            }
        })
        .OnChange((items: StationItem[]) => {
            if (this.channels && !this.stations.isOpened) {
                this.updateChannels();
            }
            if (items.length === 0) {
                return;
            }
        })
        .CompareFn((lhs: StationItem, rhs: StationItem) => lhs.id === rhs.id)
        .Required(false)
        .Toggle(this.isStationsToggle);

    updateStations(): void {
        const newOptions = this.stationItems.filter((i) => this.markets.Value.map((market) => market.id).includes(i.marketId));
        cascadeToFilter(this.stations, newOptions);
    }

    channels = channelsFilter(this.settings)
        .SetName('channels')
        .OnLoad((items: ChannelItem[]) => {
            if (this.channelItems.length === 0) {
                this.channelItems = cloneDeep(items);
            }
            this.updateChannels();
        })
        .OnChange((items: LookupItem[]) => {
            if (items.length === 0) {
                return;
            }
        })
        .Required(false)
        .Toggle(this.isChannelsToggle);

    updateChannels(): void {
        const newOptions = this.channelItems.filter(
            (i) => this.stations.Value.map((item) => item.id).includes(i.stationId) && this.channelGroupId === i.channelGroup,
        ); // need to verify
        this.channels.Options(newOptions);
        this.channels.Default(newOptions);
    }
}
